import { ChildActionsUnionUnion, PlanningActionMetadataDtoModelType, PlanningActionsStatus } from '../../../models';
import getAppRoot from '../../../utils/getAppRoot';
import { TopMenuView } from '../../../../../components/scheduling/ViewMenu';
import { handleUnscheduleNotification } from '../../handlers/planningActions/defaultHandler';

/** True when the action's day is the page date, or the open scheduling-preview day. */
const isActionDateInScope = (
  appStore: ReturnType<typeof getAppRoot>,
  startDate: string | null | undefined,
) => {
  if (!startDate) return false;
  if (appStore.ui.selectedDate === startDate) return true;
  return Boolean(
    appStore.ui.showSchedulePreview
    && appStore.schedulingWorkflow.targetStartDate === startDate,
  );
};

const bulkUpdateVisitIsUnscheduling = ({ store, ...planningAction }: PlanningActionMetadataDtoModelType, isUnscheduling: boolean) => {
  const appStore = getAppRoot(store);

  (planningAction.childActions as ChildActionsUnionUnion[])?.forEach((c) => {
    // eslint-disable-next-line
    if (c.__typename === 'VisitActionDto' && c.name === 'UnscheduleVisits') {
      c.eventUuids?.forEach((orderUuid: string) => {
        const orderToUpdate = appStore.scheduledVisits.find((v) => v.uuid === orderUuid);
        if (orderToUpdate) {
          const order = store.visitDtos.get(orderToUpdate.id);
          if (order && order.isScheduled) {
            order.setIsUnscheduling(isUnscheduling);
          }
        }
      });
    }
  });
};

const bulkUpdateRouteIsDeleting = ({ store, ...planningAction }: PlanningActionMetadataDtoModelType, isDeleting: boolean) => {
  (planningAction.childActions as ChildActionsUnionUnion[])?.forEach((c) => {
    // eslint-disable-next-line
    if (c.__typename === 'RouteActionDto' && c.name === 'DeleteRoutes' && c.routeUuid) {
      // `sortedRoutes` is page-date scoped; preview may delete an isolated day.
      store.routeByUuid(c.routeUuid)?.setIsDeleting(isDeleting);
    }
  });
};

const bulkUpdateRouteIsScheduling = ({ store, ...planningAction }: PlanningActionMetadataDtoModelType, isScheduling: boolean) => {
  (planningAction.childActions as ChildActionsUnionUnion[])?.forEach((c) => {
    // eslint-disable-next-line
    if (c.__typename === 'RouteActionDto' && c.name === 'ScheduleEvents' && c.routeUuid) {
      store.routeByUuid(c.routeUuid)?.setIsScheduling(isScheduling);
    }
  });
};

const bulkRemoveRoutes = ({ store, ...planningAction }: PlanningActionMetadataDtoModelType) => {
  const appStore = getAppRoot(store);

  (planningAction.childActions as ChildActionsUnionUnion[])?.forEach((c) => {
    // eslint-disable-next-line
    if (c.__typename === 'RouteActionDto' && c.routeUuid) {
      const route = store.routeByUuid(c.routeUuid);
      if (route?.id) {
        if (appStore.ui.selectedRoute?.id === route.id) {
          appStore.ui.selectRoute(null);
        }

        store.remove(route);
      }
    }
  });
};

export default ({ store, ...planningAction }: PlanningActionMetadataDtoModelType) => async () => {
  const appStore = getAppRoot(store);
  const projectId = appStore.ui.selectedWorkspace?.id;
  const { selectedDate } = appStore.ui;

  if (!projectId || !isActionDateInScope(appStore, planningAction.startDate)) {
    return;
  }

  if (planningAction.isUndo) {
    if (planningAction.status === PlanningActionsStatus.NOT_STARTED) {
      await appStore.model.fetchProjectRoutes(projectId, appStore.ui.selectedDate);
      bulkUpdateRouteIsScheduling({ store, ...planningAction }, true);
    }

    if (planningAction.status === PlanningActionsStatus.FINISHED) {
      await appStore.model.fetchUnscheduledVisitsLatLngs(projectId);

      appStore.model.fetchProjectRoutes(projectId, appStore.ui.selectedDate);
      appStore.model.fetchProjectRoutesExtraData(projectId, appStore.ui.selectedDate);

      bulkUpdateRouteIsScheduling({ store, ...planningAction }, false);

      appStore.ui.setCurrentView(TopMenuView.SCHEDULED);

      appStore.planningActions.getUndoableRedoableActions(true);
    }

    return;
  }

  switch (planningAction.status) {
    case PlanningActionsStatus.NOT_STARTED:
    case PlanningActionsStatus.IN_PROGRESS: {
      bulkUpdateVisitIsUnscheduling({ store, ...planningAction }, true);
      bulkUpdateRouteIsDeleting({ store, ...planningAction }, true);
      break;
    }
    case PlanningActionsStatus.FINISHED: {
      await appStore.model.fetchUnscheduledVisitsLatLngs(projectId);

      bulkRemoveRoutes({ store, ...planningAction });

      appStore.planningActions.getUndoableRedoableActions(true);

      const earliestDate = planningAction.output?.details?.earliestDate;

      if (selectedDate && earliestDate && earliestDate !== selectedDate) {
        handleUnscheduleNotification(
          selectedDate,
          store,
          appStore,
          planningAction.output?.details?.unscheduledOrdersCount,
          earliestDate,
        );
      }

      break;
    }

    case PlanningActionsStatus.FAILED:
    case PlanningActionsStatus.FINISHED_WITH_FAILED: {
      bulkUpdateVisitIsUnscheduling({ store, ...planningAction }, false);
      bulkUpdateRouteIsDeleting({ store, ...planningAction }, false);

      appStore.planningActions.getUndoableRedoableActions(true);

      break;
    }
    default: {
      break;
    }
  }
};
